/**
 * Proof strip "Auto-scrolling row" on phones: the row scrolls itself at
 * data-marquee-speed px/s and can be swiped. A second copy of the cells follows
 * the first, so jumping back by half the row's width when the scroll passes it
 * (or forward when a swipe runs off the start) is invisible. A touch pauses it;
 * it carries on a moment after the finger lifts and any momentum settles.
 * Off above 749px and with reduced motion (the CSS leaves a plain swipeable row).
 */
(() => {
  const PHONE = window.matchMedia('(max-width: 749px)');
  const STILL = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RESUME_MS = 1800;

  /** @param {HTMLElement} section */
  function init(section) {
    if (section.dataset.marqueeReady !== undefined || STILL.matches) return;
    const list = section.querySelector('.proof-strip__list');
    if (!(list instanceof HTMLElement)) return;

    section.toggleAttribute('data-marquee-ready', true);
    const speed = Number(section.dataset.marqueeSpeed) || 50;
    let pos = 0;
    let last = 0;
    let holdUntil = 0;
    let touching = false;
    let visible = false;
    let frame = 0;

    const half = () => list.scrollWidth / 2;

    // Start on the copy, so a swipe back from the first cell has somewhere to go
    const start = () => {
      pos = half();
      list.scrollLeft = pos;
    };

    const wrap = () => {
      const h = half();
      if (h <= 0) return;
      if (list.scrollLeft >= h * 2 - list.clientWidth - 1) list.scrollLeft -= h;
      else if (list.scrollLeft <= 0) list.scrollLeft += h;
    };

    const hold = () => {
      holdUntil = performance.now() + RESUME_MS;
    };

    /** @param {number} now */
    const tick = (now) => {
      frame = 0;
      if (!visible || !PHONE.matches) return;
      const dt = last ? Math.min(now - last, 100) / 1000 : 0;
      last = now;
      if (!touching && now >= holdUntil) {
        pos += speed * dt;
        const h = half();
        if (h > 0 && pos >= h * 2 - list.clientWidth) pos -= h;
        list.scrollLeft = pos;
      } else {
        pos = list.scrollLeft;
      }
      frame = requestAnimationFrame(tick);
    };

    const run = () => {
      if (frame || !visible || !PHONE.matches) return;
      last = 0;
      pos = list.scrollLeft;
      frame = requestAnimationFrame(tick);
    };

    list.addEventListener('touchstart', () => { touching = true; hold(); }, { passive: true });
    list.addEventListener('touchend', () => { touching = false; hold(); }, { passive: true });
    list.addEventListener('touchcancel', () => { touching = false; hold(); }, { passive: true });
    list.addEventListener('pointerdown', hold, { passive: true });
    list.addEventListener('wheel', hold, { passive: true });
    list.addEventListener('scroll', () => {
      if (touching || performance.now() < holdUntil) {
        wrap();
        pos = list.scrollLeft;
        if (touching) hold();
      }
    }, { passive: true });

    new IntersectionObserver((entries) => {
      visible = entries.some((e) => e.isIntersecting);
      run();
    }).observe(section);

    PHONE.addEventListener('change', () => { if (PHONE.matches) { start(); run(); } });
    if (PHONE.matches) start();
  }

  const scan = () => document.querySelectorAll('.proof-strip--m-marquee[data-marquee-speed]').forEach((el) => init(/** @type {HTMLElement} */ (el)));
  scan();
  document.addEventListener('shopify:section:load', scan);
})();
